/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  TRACE_VERSION,
  type TraceRuntimeSnapshot,
  type StoredTraceSession,
} from "./types";

interface NetworkInformationLike {
  type?: string;
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
}

/** Возвращает URL без path, query и fragment. */
export function safeOrigin(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return new URL(value, window.location.href).origin;
  } catch {
    return null;
  }
}

/** Снимает доступный браузеру сетевой контекст без дополнительных запросов. */
export function getRuntimeSnapshot(): TraceRuntimeSnapshot {
  const connection = (
    navigator as Navigator & { connection?: NetworkInformationLike }
  ).connection;
  return {
    online: navigator.onLine,
    visibilityState: document.visibilityState,
    networkType: connection?.type ?? null,
    effectiveConnectionType: connection?.effectiveType ?? null,
    rtt: Number.isFinite(connection?.rtt) ? connection!.rtt! : null,
    downlink: Number.isFinite(connection?.downlink)
      ? connection!.downlink!
      : null,
  };
}

/** Классифицирует окружение консервативно, не считая любой iframe webview. */
export function createInitialSession(sessionId: string): StoredTraceSession {
  const ua = navigator.userAgent;
  const mobile = /Android|iPhone|iPad|iPod/i.test(ua);
  const android = /Android/i.test(ua);
  const ios = /iPhone|iPad|iPod/i.test(ua);
  const webview = /; wv\)|\bwv\b|WebView|FBAN|FBAV/i.test(ua);
  const desktopAppWebview = /Electron/i.test(ua);

  let hostPlatform: StoredTraceSession["hostPlatform"] = "unknown";
  if (android) hostPlatform = "android";
  else if (ios) hostPlatform = "ios";
  else if (/Windows/i.test(ua)) hostPlatform = "windows";
  else if (/Macintosh|Mac OS X/i.test(ua)) hostPlatform = "macos";
  else if (/Linux/i.test(ua)) hostPlatform = "linux";

  let browserFamily: StoredTraceSession["browserFamily"] = "unknown";
  if (webview || desktopAppWebview) browserFamily = "webview";
  else if (/Edg\//i.test(ua)) browserFamily = "edge";
  else if (/Firefox\//i.test(ua)) browserFamily = "firefox";
  else if (/Chrome\//i.test(ua)) browserFamily = "chrome";
  else if (/Safari\//i.test(ua)) browserFamily = "safari";

  const appMode =
    import.meta.env.VITE_PACKAGE === "embedded" ? "embedded" : "standalone";
  const browserContext: StoredTraceSession["browserContext"] = desktopAppWebview
    ? "desktop-app-webview"
    : webview
      ? "mobile-app-webview"
      : mobile
        ? "mobile-browser"
        : "desktop-browser";
  const now = new Date().toISOString();

  return {
    sessionId,
    traceVersion: TRACE_VERSION,
    projectionRevision: 0,
    startedAt: now,
    endedAt: null,
    lastSequence: 0,
    endReason: null,
    appMode,
    platformFamily: android
      ? "android"
      : ios
        ? "ios"
        : desktopAppWebview
          ? "desktop"
          : "web",
    hostPlatform,
    browserFamily,
    browserContext,
    homeserver: null,
    backendBaseUrl: null,
    livekitBaseUrl: null,
    buildVersion: import.meta.env.VITE_APP_VERSION || "dev",
    gitSha: import.meta.env.VITE_GIT_SHA || null,
    ...getRuntimeSnapshot(),
    createdAt: now,
    lastEventAt: now,
    approximateBytes: 0,
  };
}

/** Создаёт нестабильную между session ссылку на browser media device. */
export async function hashMediaDeviceId(
  sessionId: string,
  deviceId: string | undefined,
): Promise<string | null> {
  if (!deviceId) return null;
  const bytes = new TextEncoder().encode(`${sessionId}:${deviceId}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest).slice(0, 12), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}
